import type { Uid } from "@/helpers/utility/Id";
import { useSceneTree } from "./sceneTree";
import { useSceneNodesStore } from "./sceneNodes.store";
import type { SceneTreeNode } from "./SceneTreeItem";
import type { SceneNodeItem } from "./SceneNodeItem";

export function getHighestParentNodes(sceneNodes: SceneNodeItem[]): SceneNodeItem[] {
  const sceneTree = useSceneTree();

  const highestParents: SceneNodeItem[] = [];

  let highestLevel = Infinity;

  for (const node of sceneNodes) {
    // If there's a node with no parent, we are on the highest level,
    // so return an empty array.
    if (node.parentSceneNodeUid == null) return [];

    const parentNode = sceneTree.treeNodes.get(node.parentSceneNodeUid);
    if (!parentNode) continue;

    if (parentNode.level < highestLevel) {
      highestLevel = parentNode.level;
      highestParents.length = 0; // Clear the array
      highestParents.push(parentNode.node);
    } else if (parentNode.level === highestLevel) {
      highestParents.push(parentNode.node);
    }
  }

  return highestParents;
}

export function getHighestLevelNodes(treeNodes: SceneTreeNode[]): SceneTreeNode[] {
  let highestLevel = Infinity;
  for (const node of treeNodes) {
    if (node.level < highestLevel) {
      highestLevel = node.level;
    }
  }
  return treeNodes.filter((node) => node.level === highestLevel);
}

export function getAllDescendantUids(node: SceneNodeItem): Set<Uid<"sceneNode">> {
  const sceneNodeStore = useSceneNodesStore();
  const descendantUids = new Set<Uid<"sceneNode">>();

  function collectDescendants(currentNode: SceneNodeItem) {
    const children = sceneNodeStore.byParentSceneNodeUid[currentNode.uid] || [];
    for (const child of children) {
      descendantUids.add(child.uid);
      collectDescendants(child);
    }
  }

  collectDescendants(node);
  return descendantUids;
}

/**
 * Get the dimensions of a scene tree node, including all its children
 * @param treeNode The scene tree node
 */
export function getNodeDimensions(treeNode: SceneTreeNode): { x: number; y: number; width: number; height: number } {
  if (treeNode.children.length === 0) {
    const sceneNode = treeNode.node;
    if (!("x" in sceneNode) || !("y" in sceneNode)) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    return {
      x: sceneNode.x,
      y: sceneNode.y,
      width: ("width" in sceneNode) ? sceneNode.width : 0,
      height: ("height" in sceneNode) ? sceneNode.height : 0,
    };
  }
  
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const childNode of treeNode.children) {
    const dim = getNodeDimensions(childNode);
    minX = Math.min(minX, dim.x);
    minY = Math.min(minY, dim.y);
    maxX = Math.max(maxX, dim.x + dim.width);
    maxY = Math.max(maxY, dim.y + dim.height);
  }
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
